<script setup>
// The one "Export PDF" dialog shell, used by the competition PDFs (ladder,
// league cycle, tournament, americano) AND the meet printout, so the consent
// gate cannot drift between them (owner decision, 7 Oct 2026).
//
// It owns, in this order: the title, one line saying what the sheet holds
// (description slot), the preview (preview slot), "Before you print" with the
// permission tick and, only when a name would be shortened, the under 18
// tick, then Cancel, Download and Print. Both ticks are cleared every time it
// opens. Download and Print stay disabled until the permission tick is given,
// and emit only then, with the tick passed on so the caller can check again.
//
// The preview slot must never hold the PDF itself: a PDF shown in the browser
// carries the browser's own Download and Print, which go round the tick. Show
// page images, or an HTML sheet, and fetch the PDF only in the print and
// download handlers (useExportPdf.js).
import {computed, ref, watch} from 'vue';
import TlDialog from '~/components/TlDialog.vue';
import {exportPdfCanAct} from '~/composables/useExportPdf';
import {
  PRINT_JUNIOR_FULL_NAMES_LABEL,
  PRINT_PERMISSION_LABEL,
  PRINT_PERMISSIONS_HEADING,
  PRINT_PREVIEW_HEADING,
} from '~/composables/useMeetPrintout';

const props = defineProps({
  modelValue: {type: Boolean, default: false},
  title: {type: String, required: true},
  maxWidth: {type: [String, Number], default: 720},
  description: {type: String, default: ''},
  // Something to print or save is in hand (the preview has loaded).
  ready: {type: Boolean, default: false},
  // Draw the preview and the ticks; false shows the `instead` slot (still
  // loading, or nothing to print yet).
  showContent: {type: Boolean, default: true},
  // Offer the under 18 tick: only when a name would be shortened.
  offerJuniorTick: {type: Boolean, default: false},
  juniorFullNames: {type: Boolean, default: false},
  downloadable: {type: Boolean, default: false},
  printLabel: {type: String, default: 'Print'},
  downloadLabel: {type: String, default: 'Download'},
  // 'print' | 'download' while that action fetches its file.
  busy: {type: String, default: null},
  error: {type: String, default: null},
  previewLabel: {type: String, default: 'Preview'},
  testidPrefix: {type: String, default: 'export-pdf'},
});
const emit = defineEmits(['update:modelValue', 'update:juniorFullNames', 'print', 'download']);

const ticked = ref(false);

watch(() => props.modelValue, (open) => {
  if (!open) return;
  ticked.value = false;
  emit('update:juniorFullNames', false);
}, {immediate: true});

const canAct = computed(() => exportPdfCanAct(ticked.value, props.ready && props.showContent && props.busy == null));

const onPrint = () => {
  if (!canAct.value) return;
  emit('print', {ticked: ticked.value});
};

const onDownload = () => {
  if (!canAct.value) return;
  emit('download', {ticked: ticked.value});
};

const close = () => emit('update:modelValue', false);
</script>

<template>
  <tl-dialog
      :model-value="modelValue"
      :title="title"
      :max-width="maxWidth"
      @update:model-value="emit('update:modelValue', $event)"
  >
    <slot name="before"/>

    <template v-if="showContent">
      <p class="ep-dialog__line"><slot name="description">{{ description }}</slot></p>

      <div class="tl-label mb-2">{{ PRINT_PREVIEW_HEADING }}</div>
      <div class="ep-dialog__desk" :aria-label="previewLabel" @contextmenu.prevent>
        <slot name="preview"/>
      </div>

      <div class="tl-label mb-2 mt-5">{{ PRINT_PERMISSIONS_HEADING }}</div>
      <div class="ep-dialog__ticks">
        <v-checkbox v-model="ticked" density="compact" hide-details :data-testid="testidPrefix + '-permission'">
          <template #label><span class="ep-dialog__tick">{{ PRINT_PERMISSION_LABEL }}</span></template>
        </v-checkbox>
        <v-checkbox v-if="offerJuniorTick" :model-value="juniorFullNames" density="compact" hide-details
                    :data-testid="testidPrefix + '-junior-full-names'"
                    @update:model-value="emit('update:juniorFullNames', $event === true)">
          <template #label><span class="ep-dialog__tick">{{ PRINT_JUNIOR_FULL_NAMES_LABEL }}</span></template>
        </v-checkbox>
      </div>

      <v-alert v-if="error" type="error" variant="tonal" density="compact" class="mt-4">{{ error }}</v-alert>
    </template>
    <slot v-else name="instead"/>

    <template #actions>
      <v-btn variant="text" @click="close">Cancel</v-btn>
      <v-spacer/>
      <v-btn v-if="downloadable" variant="outlined" prepend-icon="mdi-download"
             :disabled="!canAct" :loading="busy === 'download'"
             :data-testid="testidPrefix + '-download'"
             @click="onDownload">{{ downloadLabel }}</v-btn>
      <v-btn color="primary" variant="flat" prepend-icon="mdi-printer"
             :disabled="!canAct" :loading="busy === 'print'"
             :data-testid="testidPrefix + '-print'"
             @click="onPrint">{{ printLabel }}</v-btn>
    </template>

    <slot name="outside"/>
  </tl-dialog>
</template>

<style scoped lang="scss">
.ep-dialog__line {
  font-size: 13.5px;
  line-height: 1.55;
  color: rgb(var(--v-theme-charcoal));
  margin-bottom: 16px;
}

/* The preview: the sheet's pages lying on a grey desk, one under another,
   scrolled when it runs over a page. */
.ep-dialog__desk {
  position: relative;
  max-height: 52vh;
  overflow-y: auto;
  padding: 16px;
  background: rgb(var(--v-theme-borderLight));
  border-radius: 10px;
}

.ep-dialog__ticks {
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.ep-dialog__tick {
  font-size: 13.5px;
  font-weight: 500;
  line-height: 1.45;
}
</style>
